import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Button from '../components/Button'

const WellnessOnboarding = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState(0) // 1 for forward, -1 for backward
  const [formData, setFormData] = useState({
    wellnessInterest: '',
    experienceLevel: '',
    practiceGoal: '',
    targetAilment: '',
    ailmentStrengthScore: '',
    overallWellbeingScore: '',
  })

  const [errors, setErrors] = useState({})

  // Validation functions
  const validateWellnessInterest = (interest) => {
    if (!interest.trim()) return 'Please select what you\'re interested in'
    return ''
  }

  const validateExperienceLevel = (level) => {
    if (!level.trim()) return 'Please select your experience level'
    return ''
  }

  const validatePracticeGoal = (goal) => {
    if (!goal.trim()) return 'Please select your main goal'
    return ''
  }

  const validateTargetAilment = (ailment) => {
    if (!ailment.trim()) return 'Please select the area you\'d like to work on'
    return ''
  }

  const validateAilmentStrengthScore = (score) => {
    if (!score.trim()) return 'Please rate the current strength of this area'
    const numScore = parseInt(score)
    if (isNaN(numScore) || numScore < 1 || numScore > 10) {
      return 'Please enter a score between 1 and 10'
    }
    return ''
  }

  const validateOverallWellbeingScore = (score) => {
    if (!score.trim()) return 'Please rate your overall wellbeing'
    const numScore = parseInt(score)
    if (isNaN(numScore) || numScore < 1 || numScore > 10) {
      return 'Please enter a score between 1 and 10'
    }
    return ''
  }

  // Step configuration - dynamic based on wellness interest
  const getSteps = () => {
    const baseSteps = [
      {
        id: 'wellnessInterest',
        title: "What are you most interested in exploring?",
        subtitle: 'Choose what resonates with you',
        field: 'wellnessInterest',
        type: 'cards',
        validate: validateWellnessInterest,
        options: [
          { value: 'yoga', label: 'Yoga', icon: '🧘‍♀️', description: 'Physical postures and mindful movement' },
          { value: 'meditation', label: 'Meditation', icon: '💡', description: 'Mindfulness and inner peace' },
          { value: 'everything', label: 'A bit of everything', icon: '✨', description: 'Explore all modalities' },
          { value: 'not-sure', label: "I'm not sure", icon: '🤔', description: 'Help me discover what fits' },
        ],
      },
    ]

    // Add conditional steps for Yoga or Meditation
    if (formData.wellnessInterest === 'yoga' || formData.wellnessInterest === 'meditation') {
      const practiceLabel = formData.wellnessInterest === 'yoga' ? 'Yoga' : 'Meditation'
      
      // Define goal options based on practice type
      const goalOptions = formData.wellnessInterest === 'yoga' 
        ? [
            { value: 'stress-relief', label: 'Stress Relief', icon: '🌊', description: 'Release tension and calm the mind' },
            { value: 'physical-fitness', label: 'Physical Fitness', icon: '💪', description: 'Improve strength, balance, and flexibility' },
            { value: 'mental-clarity', label: 'Mental Clarity', icon: '🧠', description: 'Enhance focus and mindfulness through movement' },
            { value: 'spiritual-growth', label: 'Spiritual Growth', icon: '✨', description: 'Connect body, mind, and spirit through practice' },
            { value: 'better-sleep', label: 'Better Sleep', icon: '🌙', description: 'Relax the body and prepare for restful sleep' },
            { value: 'overall-wellness', label: 'Overall Wellness', icon: '🌟', description: 'Cultivate harmony and holistic wellbeing' },
          ]
        : [
            { value: 'calm-relaxation', label: 'Calm & Relaxation', icon: '🌊', description: 'Quiet the mind and ease daily stress' },
            { value: 'focus-clarity', label: 'Focus & Clarity', icon: '🧠', description: 'Sharpen awareness and stay present in the moment' },
            { value: 'emotional-balance', label: 'Emotional Balance', icon: '⚖️', description: 'Regulate emotions and cultivate inner peace' },
            { value: 'spiritual-connection', label: 'Spiritual Connection', icon: '✨', description: 'Deepen your sense of purpose and connection beyond the self' },
            { value: 'better-sleep', label: 'Better Sleep', icon: '🌙', description: 'Unwind and prepare your mind for restful sleep' },
            { value: 'self-awareness', label: 'Self-Awareness', icon: '🪞', description: 'Explore your inner world and understand your thoughts deeply' },
            { value: 'overall-wellbeing', label: 'Overall Wellbeing', icon: '🌟', description: 'Integrate calm, focus, and compassion into daily life' },
          ]
      
      baseSteps.push(
        {
          id: 'experienceLevel',
          title: `How experienced are you with ${practiceLabel}?`,
          subtitle: 'This helps us personalize your journey',
          field: 'experienceLevel',
          type: 'cards',
          validate: validateExperienceLevel,
          options: [
            { value: 'beginner', label: 'Beginner', icon: '🌱', description: 'Just starting out or less than 6 months' },
            { value: 'intermediate', label: 'Intermediate', icon: '🌿', description: '6 months to 2 years of practice' },
            { value: 'advanced', label: 'Advanced', icon: '🌳', description: 'More than 2 years of regular practice' },
            { value: 'expert', label: 'Expert', icon: '🏆', description: 'Teacher or long-term practitioner' },
          ],
        },
        {
          id: 'practiceGoal',
          title: `What's your main goal with ${practiceLabel}?`,
          subtitle: 'Select your primary intention',
          field: 'practiceGoal',
          type: 'cards',
          validate: validatePracticeGoal,
          options: goalOptions,
        }
      )

      // Add conditional steps for Physical Fitness goal in Yoga
      if (formData.wellnessInterest === 'yoga' && formData.practiceGoal === 'physical-fitness') {
        baseSteps.push(
          {
            id: 'targetAilment',
            title: 'Is there a specific area you\'d like to work on?',
            subtitle: 'Select the body area that needs attention',
            field: 'targetAilment',
            type: 'cards',
            validate: validateTargetAilment,
            options: [
              { value: 'lower-back', label: 'Lower Back', icon: '🦴', description: 'Strengthen and heal your lower back' },
              { value: 'upper-back', label: 'Upper Back', icon: '💪', description: 'Release tension and build upper back strength' },
              { value: 'shoulders', label: 'Shoulders', icon: '🤸', description: 'Improve shoulder mobility and strength' },
              { value: 'legs', label: 'Legs', icon: '🦵', description: 'Build leg strength and flexibility' },
              { value: 'hips', label: 'Hips', icon: '🧘', description: 'Open and strengthen your hip area' },
              { value: 'hands-wrists', label: 'Hands & Wrists', icon: '✋', description: 'Strengthen hands and wrist mobility' },
              { value: 'neck', label: 'Neck', icon: '🙆', description: 'Release neck tension and improve mobility' },
              { value: 'core', label: 'Core', icon: '🔥', description: 'Build core strength and stability' },
            ],
          },
          {
            id: 'ailmentStrengthScore',
            title: `How would you rate your ${formData.targetAilment ? formData.targetAilment.replace('-', ' ') : 'selected area'} strength right now?`,
            subtitle: 'Rate from 1 (very weak) to 10 (very strong)',
            field: 'ailmentStrengthScore',
            type: 'cards',
            validate: validateAilmentStrengthScore,
            options: [
              { value: '1', label: '1', icon: '😔', description: 'Very weak - significant discomfort' },
              { value: '2', label: '2', icon: '😟', description: 'Very weak' },
              { value: '3', label: '3', icon: '😕', description: 'Weak' },
              { value: '4', label: '4', icon: '😐', description: 'Below average' },
              { value: '5', label: '5', icon: '🙂', description: 'Average' },
              { value: '6', label: '6', icon: '😊', description: 'Above average' },
              { value: '7', label: '7', icon: '😄', description: 'Good' },
              { value: '8', label: '8', icon: '😃', description: 'Very good' },
              { value: '9', label: '9', icon: '🤩', description: 'Excellent' },
              { value: '10', label: '10', icon: '💪', description: 'Very strong - no issues' },
            ],
          },
          {
            id: 'overallWellbeingScore',
            title: 'How would you rate your overall wellbeing?',
            subtitle: 'Rate from 1 (poor) to 10 (excellent)',
            field: 'overallWellbeingScore',
            type: 'cards',
            validate: validateOverallWellbeingScore,
            options: [
              { value: '1', label: '1', icon: '😢', description: 'Poor - struggling significantly' },
              { value: '2', label: '2', icon: '😔', description: 'Very low' },
              { value: '3', label: '3', icon: '😕', description: 'Low' },
              { value: '4', label: '4', icon: '😐', description: 'Below average' },
              { value: '5', label: '5', icon: '🙂', description: 'Average' },
              { value: '6', label: '6', icon: '😊', description: 'Above average' },
              { value: '7', label: '7', icon: '😄', description: 'Good' },
              { value: '8', label: '8', icon: '😃', description: 'Very good' },
              { value: '9', label: '9', icon: '🤩', description: 'Excellent' },
              { value: '10', label: '10', icon: '🌟', description: 'Outstanding' },
            ],
          }
        )
      }
    }

    return baseSteps
  }

  const steps = getSteps()

  const currentStepData = steps[currentStep]

  const handleInputChange = (e) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      [currentStepData.field]: value,
    }))
    // Clear error when user starts typing
    if (errors[currentStepData.field]) {
      setErrors((prev) => ({
        ...prev,
        [currentStepData.field]: '',
      }))
    }
  }

  const handleCardSelect = (value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [currentStepData.field]: value,
      }
      
      // Reset conditional fields if wellness interest changes
      if (currentStepData.field === 'wellnessInterest') {
        newData.experienceLevel = ''
        newData.practiceGoal = ''
        newData.targetAilment = ''
        newData.ailmentStrengthScore = ''
        newData.overallWellbeingScore = ''
      }
      
      // Reset physical fitness fields if practice goal changes
      if (currentStepData.field === 'practiceGoal') {
        newData.targetAilment = ''
        newData.ailmentStrengthScore = ''
        newData.overallWellbeingScore = ''
      }
      
      return newData
    })
    // Clear error when user selects
    if (errors[currentStepData.field]) {
      setErrors((prev) => ({
        ...prev,
        [currentStepData.field]: '',
      }))
    }
  }

  const handleNext = () => {
    // Validate current field
    const error = currentStepData.validate(formData[currentStepData.field])
    
    if (error) {
      setErrors((prev) => ({
        ...prev,
        [currentStepData.field]: error,
      }))
      return
    }

    if (currentStep < steps.length - 1) {
      setDirection(1)
      setCurrentStep((prev) => prev + 1)
    } else {
      // Last step - submit the form
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep((prev) => prev - 1)
    } else {
      // Go back to basic onboarding
      navigate('/onboarding')
    }
  }

  const handleSubmit = () => {
    console.log('Wellness onboarding completed:', formData)
    
    // Determine if user is "sure" or "unsure" based on their selection
    const unsureOptions = ['everything', 'not-sure']
    
    const isUnsure = unsureOptions.includes(formData.wellnessInterest)
    
    // Combine with basic onboarding data
    const basicData = JSON.parse(localStorage.getItem('basicOnboardingData') || '{}')
    const completeData = { 
      ...basicData, 
      ...formData,
      userCertainty: isUnsure ? 'unsure' : 'sure', // Track certainty state
      selectedJourney: isUnsure ? 'exploratory' : 'focused' // Track journey type
    }
    
    console.log('Complete onboarding data:', completeData)
    
    // Save the complete onboarding data including certainty state
    localStorage.setItem('onboardingData', JSON.stringify(completeData))
    localStorage.removeItem('basicOnboardingData') // Clean up
    
    // Here you would typically send the data to your backend
    // For now, navigate to dashboard with journey information
    navigate('/dashboard', { 
      state: { 
        isNewUser: true,
        userCertainty: completeData.userCertainty,
        selectedJourney: completeData.selectedJourney,
        wellnessInterest: formData.wellnessInterest,
        experienceLevel: formData.experienceLevel,
        practiceGoal: formData.practiceGoal,
        targetAilment: formData.targetAilment,
        ailmentStrengthScore: formData.ailmentStrengthScore,
        overallWellbeingScore: formData.overallWellbeingScore
      } 
    })
  }

  // Animation variants
  const pageVariants = {
    initial: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8,
    }),
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.43, 0.13, 0.23, 0.96],
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
      },
    }),
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-4xl">
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <img 
            src="/logo.png" 
            alt="Wellness Loop Logo" 
            className="h-16 w-auto"
          />
        </div>
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Wellness Journey · Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-purple-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-white/60 rounded-full overflow-hidden backdrop-blur-sm">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: (currentStep + 1) / steps.length }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12"
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              {/* Title and subtitle */}
              <div className="text-center space-y-3">
                <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {currentStepData.title}
                </h2>
                <p className="text-lg text-gray-600">
                  {currentStepData.subtitle}
                </p>
              </div>

              {/* Cards Grid */}
              {currentStepData.type === 'cards' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentStepData.options.map((option, index) => (
                      <motion.button
                        key={option.value}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleCardSelect(option.value)}
                        className={`relative p-6 rounded-2xl border-2 text-left transition-all transform hover:scale-105 hover:shadow-lg ${
                          formData[currentStepData.field] === option.value
                            ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-purple-300'
                        }`}
                      >
                        {/* Selected indicator */}
                        {formData[currentStepData.field] === option.value && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute top-4 right-4 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center"
                          >
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </motion.div>
                        )}
                        
                        <div className="flex items-start space-x-4">
                          <div className="text-4xl">{option.icon}</div>
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-gray-900 mb-1">
                              {option.label}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {option.description}
                            </p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                  <AnimatePresence>
                    {errors[currentStepData.field] && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-red-600 text-sm text-center"
                      >
                        {errors[currentStepData.field]}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type={currentStepData.type}
                    value={formData[currentStepData.field]}
                    onChange={handleInputChange}
                    placeholder={currentStepData.placeholder}
                    autoFocus
                    className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                      errors[currentStepData.field]
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                  <AnimatePresence>
                    {errors[currentStepData.field] && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-red-600 text-sm px-2"
                      >
                        {errors[currentStepData.field]}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 py-4 text-lg"
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  className="flex-1 py-4 text-lg"
                >
                  {currentStep === steps.length - 1 ? 'Complete' : 'Continue'}
                </Button>
              </div>

              {/* Step indicators */}
              <div className="flex justify-center gap-2 pt-4">
                {steps.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'w-8 bg-gradient-to-r from-purple-600 to-pink-600'
                        : index < currentStep
                        ? 'w-2 bg-purple-400'
                        : 'w-2 bg-gray-300'
                    }`}
                    initial={false}
                    animate={{
                      scale: index === currentStep ? 1.2 : 1,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Skip option */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-6"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
          >
            Skip for now
          </button>
        </motion.div>
      </div>
    </div>
  )
}

export default WellnessOnboarding

